import { FC, Fragment, ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedDate, FormattedMessage } from 'dibs-react-intl';

import { Link } from 'dibs-elements/exports/Link';
import Hourglass from 'dibs-icons/exports/legacy/Hourglass';
import { etherscanUrl } from 'dibs-crypto-utils/exports/formattingUtils';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency';
import { useEtherConversion } from 'dibs-crypto-utils/exports/useEtherConversion';
import type { GetAmountStrings } from 'dibs-crypto-utils/exports/useEtherConversion';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { NftPdpUserLink } from '../../components/NftPdpUserLink';

import styles from './NftPdpHistoryItem.scss';
import { NftPdpHistoryItem_nftItemHistoryEvent$key } from './__generated__/NftPdpHistoryItem_nftItemHistoryEvent.graphql';

type Props = {
    nftItemHistoryEvent: NftPdpHistoryItem_nftItemHistoryEvent$key;
    getAmountStrings: GetAmountStrings;
    userWalletAddress?: string | null;
    canceledAuctionIds?: string[];
    tokenId: string;
};

function getTransactionLink(content: ReactNode[], transactionHash: string | null): ReactNode {
    return (
        <Link
            href={etherscanUrl(`/tx/${transactionHash}`)}
            target="_blank"
            className={styles.noWrap}
            key={`${transactionHash}-tx-link`}
        >
            {content}
        </Link>
    );
}

export const NftPdpHistoryItem: FC<Props> = ({
    nftItemHistoryEvent: nftItemHistoryEventRef,
    getAmountStrings,
    userWalletAddress,
    tokenId,
}) => {
    const { currency } = useCurrency();
    const nftItemHistoryEvent = useFragment(
        graphql`
            fragment NftPdpHistoryItem_nftItemHistoryEvent on NftItemHistoryEvent {
                __typename
                timestamp
                transactionHash
                value {
                    ether
                }
                ... on NftItemHistoryTransferEvent {
                    from {
                        address
                        isAddressZero
                        ...NftPdpUserLink_ethereumAddress
                    }
                    to {
                        address
                        ...NftPdpUserLink_ethereumAddress
                    }
                }
                ... on NftItemHistoryAuctionCreatedEvent {
                    auction {
                        endedConversionRates {
                            rates {
                                ...useEtherConversion_etherRates
                            }
                        }
                    }
                    reservePrice {
                        ether
                    }
                    auctionCreator {
                        ...NftPdpUserLink_ethereumAddress
                    }
                }
                ... on NftItemHistoryAuctionBidEvent {
                    auction {
                        endedConversionRates {
                            rates {
                                ...useEtherConversion_etherRates
                            }
                        }
                    }
                    bidAmount {
                        ether
                    }
                    bidder {
                        address
                        publicProfileUrl
                        ...NftPdpUserLink_ethereumAddress
                    }
                }
                ... on NftItemHistoryAuctionPausedEvent {
                    isPaused
                }
                ... on NftItemHistoryAuctionEndedEvent {
                    auction {
                        endedConversionRates {
                            rates {
                                ...useEtherConversion_etherRates
                            }
                        }
                    }
                    winningBid {
                        ether
                    }
                    winningBidder {
                        address
                        publicProfileUrl
                        ...NftPdpUserLink_ethereumAddress
                    }
                }
                ... on NftItemHistoryAuctionCanceledEvent {
                    canceledBy {
                        ...NftPdpUserLink_ethereumAddress
                    }
                }
                ... on NftItemHistoryTransferSingleEvent {
                    tokenId
                    valueArg
                    from {
                        address
                        isAddressZero
                        ...NftPdpUserLink_ethereumAddress
                    }
                    to {
                        address
                        isAddressZero
                        ...NftPdpUserLink_ethereumAddress
                    }
                    operator {
                        address
                    }
                }
                ... on NftItemHistoryTransferBatchEvent {
                    values
                    ids
                    from {
                        address
                        isAddressZero
                        ...NftPdpUserLink_ethereumAddress
                    }
                    to {
                        address
                        isAddressZero
                        ...NftPdpUserLink_ethereumAddress
                    }
                    operator {
                        address
                    }
                }
            }
        `,
        nftItemHistoryEventRef
    );
    const {
        __typename,
        timestamp,
        value,
        transactionHash,
        from,
        isPaused,
        winningBid,
        reservePrice,
        bidAmount,
        bidder,
        winningBidder,
        auctionCreator,
        to: transferTo,
        canceledBy,
        auction,
        valueArg: balance,
        ids: batchTransferIds,
        values: batchTransferValues,
    } = nftItemHistoryEvent;
    const { endedConversionRates } = auction || {};

    const { getAmountStrings: getSoldAmountStrings } = useEtherConversion({
        currency,
        etherRates: endedConversionRates?.rates
            ? endedConversionRates.rates.filter(filterNulls)
            : null,
    });

    const isMintEvent = __typename === 'NftItemHistoryTransferEvent' && from?.isAddressZero;
    const isAuctionCreatedEvent = __typename === 'NftItemHistoryAuctionCreatedEvent';
    const isBidEvent = __typename === 'NftItemHistoryAuctionBidEvent';
    const isAuctionPausedEvent = __typename === 'NftItemHistoryAuctionPausedEvent' && isPaused;
    const isAuctionUnpausedEvent = __typename === 'NftItemHistoryAuctionPausedEvent' && !isPaused;
    const isAuctionCancelledEvent = __typename === 'NftItemHistoryAuctionCanceledEvent';
    const isAuctionEndedEvent = __typename === 'NftItemHistoryAuctionEndedEvent';
    const isTransferEvent = __typename === 'NftItemHistoryTransferEvent' && !from?.isAddressZero;

    // ERC-1155 Single Editions events
    const isMintEditionsEvent =
        __typename === 'NftItemHistoryTransferSingleEvent' && from?.isAddressZero;
    const isTransferEditionsEvent =
        __typename === 'NftItemHistoryTransferSingleEvent' &&
        !from?.isAddressZero &&
        !transferTo?.isAddressZero;
    const isBurnEditionsEvent =
        __typename === 'NftItemHistoryTransferSingleEvent' && transferTo?.isAddressZero;

    // ERC-1155 Batch Editions events
    const isValidBatchArrays =
        batchTransferIds &&
        batchTransferIds.length >= 1 &&
        batchTransferValues &&
        batchTransferValues.length >= 1;
    const isMintBatchEditionsEvent =
        __typename === 'NftItemHistoryTransferBatchEvent' &&
        from?.isAddressZero &&
        isValidBatchArrays;
    const isTransferBatchEditionsEvent =
        __typename === 'NftItemHistoryTransferBatchEvent' &&
        !from?.isAddressZero &&
        !transferTo?.isAddressZero &&
        isValidBatchArrays;
    const isBurnBatchEditionsEvent =
        __typename === 'NftItemHistoryTransferBatchEvent' &&
        transferTo?.isAddressZero &&
        isValidBatchArrays;

    const isUnhandledEvent = ![
        isMintEvent,
        isAuctionCreatedEvent,
        isBidEvent,
        isAuctionPausedEvent,
        isAuctionUnpausedEvent,
        isAuctionEndedEvent,
        isAuctionCancelledEvent,
        isTransferEvent,
        isMintEditionsEvent,
        isTransferEditionsEvent,
        isBurnEditionsEvent,
        isMintBatchEditionsEvent,
        isTransferBatchEditionsEvent,
        isBurnBatchEditionsEvent,
    ].find(eventValue => eventValue);

    if (isUnhandledEvent) {
        return null;
    }

    let transactionEthValue = null;
    let transactionUser = null;

    if (isMintEvent) {
        transactionEthValue = value?.ether;
    } else if (isAuctionCreatedEvent) {
        transactionEthValue = reservePrice?.ether;
    } else if (isBidEvent) {
        transactionUser = bidder;
        transactionEthValue = bidAmount?.ether;
    } else if (isAuctionEndedEvent) {
        transactionUser = winningBidder;
        transactionEthValue = winningBid?.ether;
    }

    const { converted, ether } = endedConversionRates
        ? getSoldAmountStrings(transactionEthValue ?? '')
        : getAmountStrings(transactionEthValue ?? '');

    const bidAmountLink = (
        <Fragment key="bid-amount">
            <Link href={etherscanUrl(`/tx/${transactionHash}`)} target="_blank">
                {ether}
            </Link>{' '}
            ({converted})
        </Fragment>
    );

    return (
        <li className={styles.eventListItem}>
            <span className={styles.eventDate}>
                <FormattedDate value={timestamp || ''} dateStyle="long" timeStyle="short" />
            </span>
            <span className={styles.eventDescription}>
                {isMintEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.tokenMinted"
                        defaultMessage="<a>Minted</a> by {user}"
                        values={{
                            a: content => getTransactionLink(content, transactionHash),
                            user: (
                                <NftPdpUserLink
                                    nftEthereumAddress={transferTo ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                        }}
                    />
                )}
                {isAuctionCreatedEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.reservePriceSet"
                        defaultMessage="Reserve price set for {etherAmount} by {user}"
                        values={{
                            etherAmount: bidAmountLink,
                            user: (
                                <NftPdpUserLink
                                    nftEthereumAddress={auctionCreator ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                        }}
                    />
                )}

                {isBidEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.bidPlaced"
                        defaultMessage="Bid placed for {etherAmount} by {user}"
                        values={{
                            etherAmount: bidAmountLink,
                            user: (
                                <NftPdpUserLink
                                    nftEthereumAddress={transactionUser ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                        }}
                    />
                )}

                {isTransferEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.transferToUser"
                        defaultMessage="<a>Transfer</a> from {userFrom} to {userTo}"
                        values={{
                            a: content => getTransactionLink(content, transactionHash),
                            userFrom: (
                                <NftPdpUserLink
                                    nftEthereumAddress={from ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                            userTo: (
                                <NftPdpUserLink
                                    nftEthereumAddress={transferTo ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                        }}
                    />
                )}
                {isAuctionPausedEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.auctionPaused"
                        defaultMessage="Auction paused"
                    />
                )}

                {isAuctionUnpausedEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.auctionUnpaused"
                        defaultMessage="Auction started"
                    />
                )}
                {isAuctionCancelledEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.auctionCancelled"
                        defaultMessage="Auction canceled by {user}"
                        values={{
                            user: (
                                <NftPdpUserLink
                                    nftEthereumAddress={canceledBy ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                        }}
                    />
                )}
                {isAuctionEndedEvent && (
                    <>
                        <div className={styles.eventHeader}>
                            <Hourglass className={styles.hourglassIcon} />
                            <FormattedMessage
                                id="nft.pdp.history.event.auctionEnded"
                                defaultMessage="Auction ended"
                            />
                        </div>
                        <FormattedMessage
                            id="nft.pdp.history.event.auctionEndedSold"
                            defaultMessage="Sold for {price} to {user}"
                            values={{
                                price: bidAmountLink,
                                user: (
                                    <NftPdpUserLink
                                        nftEthereumAddress={transactionUser ?? null}
                                        userWalletAddress={userWalletAddress}
                                    />
                                ),
                            }}
                        />
                    </>
                )}
                {isMintEditionsEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.tokenEditionsMinted"
                        defaultMessage="<a>Minted</a> {balance, plural, one {# edition} other {# editions}} by {user}"
                        values={{
                            a: content => getTransactionLink(content, transactionHash),
                            user: (
                                <NftPdpUserLink
                                    nftEthereumAddress={transferTo ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                            balance,
                        }}
                    />
                )}
                {isTransferEditionsEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.transferEditionsToUser"
                        defaultMessage="<a>Transferred</a> {balance, plural, one {# edition} other {# editions}} from {userFrom} to {userTo}"
                        values={{
                            a: content => getTransactionLink(content, transactionHash),
                            userFrom: (
                                <NftPdpUserLink
                                    nftEthereumAddress={from ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                            userTo: (
                                <NftPdpUserLink
                                    nftEthereumAddress={transferTo ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                            balance,
                        }}
                    />
                )}
                {isBurnEditionsEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.tokenEditionsBurned"
                        defaultMessage="<a>Burned</a> {balance, plural, one {# edition} other {# editions}} by {user}"
                        values={{
                            a: content => getTransactionLink(content, transactionHash),
                            user: (
                                <NftPdpUserLink
                                    nftEthereumAddress={from ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                            balance,
                        }}
                    />
                )}
                {isMintBatchEditionsEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.tokenBatchEditionsMinted"
                        defaultMessage="<a>Minted</a> {balance, plural, one {# edition} other {# editions}} by {user}"
                        values={{
                            a: content => getTransactionLink(content, transactionHash),
                            user: (
                                <NftPdpUserLink
                                    nftEthereumAddress={transferTo ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                            balance: batchTransferValues[batchTransferIds.indexOf(tokenId)],
                        }}
                    />
                )}
                {isTransferBatchEditionsEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.transferBatchEditionsToUser"
                        defaultMessage="<a>Transferred</a> {balance, plural, one {# edition} other {# editions}} from {userFrom} to {userTo}"
                        values={{
                            a: content => getTransactionLink(content, transactionHash),
                            userFrom: (
                                <NftPdpUserLink
                                    nftEthereumAddress={from ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                            userTo: (
                                <NftPdpUserLink
                                    nftEthereumAddress={transferTo ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                            balance: batchTransferValues[batchTransferIds.indexOf(tokenId)],
                        }}
                    />
                )}
                {isBurnBatchEditionsEvent && (
                    <FormattedMessage
                        id="nft.pdp.history.event.tokenBatchEditionsBurned"
                        defaultMessage="<a>Burned</a> {balance, plural, one {# edition} other {# editions}} by {user}"
                        values={{
                            a: content => getTransactionLink(content, transactionHash),
                            user: (
                                <NftPdpUserLink
                                    nftEthereumAddress={from ?? null}
                                    userWalletAddress={userWalletAddress}
                                />
                            ),
                            balance: batchTransferValues[batchTransferIds.indexOf(tokenId)],
                        }}
                    />
                )}
            </span>
        </li>
    );
};
